import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { API } from "@/App";
import AdminLayout from "@/components/AdminLayout";
import { PageHeader, StatCard, StatusBadge, EmptyState, LoadingSkeleton } from "@/components/ui-system";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import {
  PackageSearch, PackagePlus, Truck, CheckCircle2, RotateCcw, Boxes, Receipt, Wallet, TrendingUp,
  ChevronRight, Plus,
} from "lucide-react";
import { formatToIST } from "@/utils/dateUtils";

const inr = (n) =>
  n == null ? "-" : `₹${Number(n).toLocaleString("en-IN", { minimumFractionDigits: 0, maximumFractionDigits: 2 })}`;

const errDetail = (error, fallback) => {
  const d = error?.response?.data?.detail;
  if (typeof d === "string") return d;
  if (Array.isArray(d) && d[0]?.msg) return d[0].msg;
  return error?.response?.data?.message || fallback;
};

// Lifecycle tiles -> orders tab group ids (shared with the orders console).
const STAT_TILES = [
  { key: "new", label: "New", icon: PackagePlus, accent: "neutral", group: "new" },
  { key: "ready", label: "Ready to Ship", icon: Boxes, accent: "info", group: "ready" },
  { key: "pickup", label: "Pickup & Manifest", icon: Truck, accent: "info", group: "pickup" },
  { key: "transit", label: "In Transit", icon: Truck, accent: "warning", group: "transit" },
  { key: "delivered", label: "Delivered", icon: CheckCircle2, accent: "success", group: "delivered" },
  { key: "rto", label: "RTO", icon: RotateCcw, accent: "danger", group: "rto" },
];

const B2CAdminDashboard = ({ user, onLogout }) => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState({ counts: {}, summary: {}, items: [], total: 0 });

  const fetchDashboard = useCallback(async () => {
    setLoading(true);
    try {
      // Reuse the cross-seller orders endpoint: it returns lifecycle counts, the
      // revenue/cost/margin summary, and a page of recent orders. No date filter
      // here = all-time overview.
      const { data: res } = await axios.get(`${API}/b2c/admin/orders`, { params: { page_size: 8 } });
      setData({
        counts: res.counts || {},
        summary: res.summary || {},
        items: Array.isArray(res.items) ? res.items : [],
        total: res.total || 0,
      });
    } catch (e) {
      toast.error(errDetail(e, "Failed to load B2C dashboard"));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchDashboard();
  }, [fetchDashboard]);

  const goTab = (group) => navigate(`/admin/b2c/orders?status_group=${group}`);
  const summary = data.summary || {};
  const marginTotal = summary.margin_total;
  const marginAccent = marginTotal == null ? "neutral" : marginTotal >= 0 ? "success" : "danger";
  const marginPctLine = summary.margin_pct != null ? `${Number(summary.margin_pct).toLocaleString("en-IN")}% of revenue` : undefined;

  return (
    <AdminLayout user={user} onLogout={onLogout}>
      <div className="space-y-6">
        <PageHeader
          title="B2C Dashboard"
          subtitle="Cross-seller B2C last-mile overview — volume, revenue and margin"
          actions={
            <Button variant="outline" onClick={() => navigate("/admin/b2c/orders")} data-testid="b2c-admin-view-orders">
              View all orders <ChevronRight className="w-4 h-4 ml-1" />
            </Button>
          }
        />

        {/* Money summary (all-time, cross-seller) */}
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
          <StatCard label="Total Orders" value={Number(summary.order_count ?? data.total ?? 0).toLocaleString("en-IN")} icon={PackageSearch} accent="brand" loading={loading} />
          <StatCard label="Revenue" value={inr(summary.revenue_total ?? 0)} icon={Receipt} accent="info" loading={loading} trend="Charged to sellers" />
          <StatCard label="Aggregator Cost" value={inr(summary.cost_total ?? 0)} icon={Wallet} accent="warning" loading={loading} trend="Paid to aggregators" />
          <StatCard label="Margin" value={inr(marginTotal ?? 0)} icon={TrendingUp} accent={marginAccent} loading={loading} trend={marginPctLine} />
        </div>

        {/* Lifecycle split (click-through to filtered orders) */}
        <div className="grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-6">
          {STAT_TILES.map((t) => (
            <StatCard
              key={t.key}
              label={t.label}
              value={loading ? "" : (data.counts[t.key] ?? 0)}
              icon={t.icon}
              accent={t.accent}
              loading={loading}
              onClick={() => goTab(t.group)}
            />
          ))}
        </div>

        {/* Recent orders */}
        <Card className="border-0 shadow-sm">
          <CardHeader className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between space-y-0">
            <div>
              <CardTitle className="text-lg">Recent B2C orders</CardTitle>
              <CardDescription>Latest shipments across all sellers.</CardDescription>
            </div>
            <Button variant="ghost" size="sm" onClick={() => navigate("/admin/b2c/orders")} data-testid="b2c-admin-view-all">
              View all <ChevronRight className="w-4 h-4 ml-1" />
            </Button>
          </CardHeader>
          <CardContent>
            {loading ? (
              <LoadingSkeleton variant="table" rows={5} cols={4} />
            ) : data.items.length === 0 ? (
              <EmptyState
                icon={PackageSearch}
                title="No B2C orders yet"
                description="Once sellers start creating B2C shipments, they'll appear here."
              />
            ) : (
              <ul className="divide-y divide-slate-100">
                {data.items.map((o) => (
                  <li key={o.id}>
                    <button
                      onClick={() => navigate(`/admin/b2c/orders/${o.id}`)}
                      className="flex w-full items-center justify-between gap-3 py-3 text-left hover:bg-slate-50 rounded-md px-2 -mx-2"
                      data-testid={`b2c-admin-recent-${o.id}`}
                    >
                      <div className="min-w-0">
                        <p className="text-sm font-medium text-slate-800 truncate">
                          {o.order_number}
                          <span className="font-normal text-slate-500"> · {o.seller_brand_name || o.seller_id}</span>
                        </p>
                        <p className="text-xs text-slate-500 truncate">
                          {o.buyer?.name || "-"} · {[o.buyer?.city, o.buyer?.pincode].filter(Boolean).join(", ")} · {formatToIST(o.created_at)}
                        </p>
                      </div>
                      <div className="flex items-center gap-3 shrink-0">
                        {o.margin != null && (
                          <span className={`text-sm font-medium tabular-nums ${o.margin >= 0 ? "text-green-700" : "text-red-600"}`}>
                            {inr(o.margin)}
                          </span>
                        )}
                        <StatusBadge status={o.status} kind="b2c" size="sm" />
                      </div>
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </CardContent>
        </Card>
      </div>
    </AdminLayout>
  );
};

export default B2CAdminDashboard;
